import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Loader2 } from 'lucide-react';
import { runnerApi } from '../../services/api';
import {
  waysApi,
  type WaysReportedSession, type WaysRunnerUsageSession, type WaysUsage as Usage,
} from '../../services/waysApi';
import { fmtTokens, tokensOf } from './waysTokens';

const RANGES = [1, 7, 30, 90];

/**
 * Token usage (PO92-225, hs-pmo/requests#5), two sources side by side and never added
 * together as if they were one: runners are MEASURED by claude-runner; interactive
 * sessions are REPORTED by the hsinc plugin and labelled so.
 */
export default function WaysUsage({ projectName }: { projectName: (id?: string | null) => string }) {
  const [days, setDays] = useState(7);
  const [runner, setRunner] = useState<Usage<WaysRunnerUsageSession> | null>(null);
  const [reported, setReported] = useState<Usage<WaysReportedSession> | null>(null);
  const [budgets, setBudgets] = useState<Record<string, number | null>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, p] = await Promise.allSettled([waysApi.runnerUsage(days), waysApi.reportedUsage(days)]);
    setRunner(r.status === 'fulfilled' ? r.value : null);
    setReported(p.status === 'fulfilled' ? p.value : null);
    setErrors([r, p].flatMap((x, i) => x.status === 'rejected'
      ? [`${i ? 'Reported' : 'Runner'} usage: ${(x.reason as Error)?.message || 'unavailable'}`] : []));
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const projectIds = useMemo(() => {
    const ids = new Set<string>();
    runner?.projects.forEach((p) => ids.add(p.project));
    reported?.projects.forEach((p) => ids.add(p.project));
    return [...ids];
  }, [runner, reported]);

  useEffect(() => {
    // The runner budget lives on PMO's enablement record, one project at a time.
    projectIds.slice(0, 40).forEach((id) => {
      if (id in budgets) return;
      runnerApi.get(id)
        .then((e) => setBudgets((b) => ({ ...b, [id]: e.enabled ? e.dailyTokenBudget : null })))
        .catch(() => setBudgets((b) => ({ ...b, [id]: null })));
    });
  }, [projectIds, budgets]);

  const byDay = useMemo(() => {
    const m: Record<string, { runner: number; reported: number }> = {};
    runner?.days.forEach((d) => { (m[d.date] ||= { runner: 0, reported: 0 }).runner += tokensOf(d.tokens); });
    reported?.days.forEach((d) => { (m[d.date] ||= { runner: 0, reported: 0 }).reported += tokensOf(d.tokens); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [runner, reported]);
  const peak = Math.max(1, ...byDay.map(([, v]) => Math.max(v.runner, v.reported)));

  const rows = useMemo(() => projectIds.map((id) => {
    const r = runner?.projects.find((p) => p.project === id);
    const p = reported?.projects.find((x) => x.project === id);
    return { id, key: r?.projectKey || p?.projectKey || projectName(id),
             runner: tokensOf(r?.tokens), reported: tokensOf(p?.tokens) };
  }).sort((a, b) => (b.runner + b.reported) - (a.runner + a.reported)), [projectIds, runner, reported, projectName]);

  const th = 'py-1.5 pr-3 font-medium';
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-accent" /> Usage
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
        </h2>
        <div className="flex gap-1">
          {RANGES.map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)}
              className={`px-2 py-0.5 text-xs rounded ${d === days ? 'bg-accent text-white' : 'text-gray-500 hover:text-accent'}`}>
              {d === 1 ? 'Today' : `${d}d`}
            </button>
          ))}
        </div>
      </div>
      {errors.map((e) => <p key={e} className="text-xs text-amber-600 dark:text-amber-500">{e}</p>)}

      <section>
        <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
          Per day <span className="text-gray-400">· <span className="text-accent">■</span> runners (measured) · <span className="text-gray-400">■</span> interactive (reported)</span>
        </h3>
        {byDay.length === 0 ? <p className="text-xs text-gray-400">No usage in this range.</p> : (
          <div className="space-y-1">
            {byDay.map(([date, v]) => (
              <div key={date} className="flex items-center gap-2 text-[11px]">
                <span className="w-12 shrink-0 font-mono text-gray-400">{date.slice(5)}</span>
                <div className="flex-1 space-y-px">
                  <div className="h-2 rounded-sm bg-accent" style={{ width: `${(v.runner / peak) * 100}%` }} />
                  <div className="h-2 rounded-sm bg-gray-300 dark:bg-gray-600" style={{ width: `${(v.reported / peak) * 100}%` }} />
                </div>
                <span className="w-28 shrink-0 text-right font-mono text-gray-500">
                  {fmtTokens(v.runner)} · {fmtTokens(v.reported)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Per project</h3>
        <table className="w-full text-xs">
          <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className={th}>Project</th>
              <th className={`${th} text-right`}>Runners</th>
              <th className={`${th} text-right`}>Reported</th>
              <th className={`${th} text-right`}>Runner budget / day</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="py-1.5 pr-3">{r.key}</td>
                <td className="py-1.5 pr-3 text-right font-mono">{fmtTokens(r.runner)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-500">{fmtTokens(r.reported)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-500">
                  {budgets[r.id] == null ? 'none' : fmtTokens(budgets[r.id] as number)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Sessions</h3>
        <table className="w-full text-xs">
          <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className={th}>Source</th>
              <th className={th}>Session</th>
              <th className={th}>Project</th>
              <th className={th}>Who · where</th>
              <th className={`${th} text-right`}>Tokens</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
            {(runner?.sessions || []).map((s, i) => (
              <tr key={`r${s.id || s.session || i}`}>
                <td className="py-1.5 pr-3">runner</td>
                <td className="py-1.5 pr-3 font-mono">{s.id || s.session}</td>
                <td className="py-1.5 pr-3">{s.projectKey || projectName(s.project)}</td>
                <td className="py-1.5 pr-3">
                  {s.item || s.itemKey ? (
                    <Link to={`/item/${s.item || s.itemKey}`} className="text-accent hover:underline" title={s.item || undefined}>
                      {s.itemKey || s.item}
                    </Link>
                  ) : '—'}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono">{fmtTokens(tokensOf(s.usage ?? s.tokens))}</td>
              </tr>
            ))}
            {(reported?.sessions || []).map((s) => (
              <tr key={`p${s.session}`}>
                <td className="py-1.5 pr-3 text-gray-500">reported</td>
                <td className="py-1.5 pr-3 font-mono">{s.session}</td>
                <td className="py-1.5 pr-3">{projectName(s.project)}</td>
                <td className="py-1.5 pr-3">
                  {[s.initials, s.client, s.repository, s.model].filter(Boolean).join(' · ')}
                  {s.turns != null && <span className="text-gray-400"> · {s.turns} turns</span>}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-500">{fmtTokens(tokensOf(s.tokens))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
